import {
    isIsoAndUnix,
    isIsoAndUnixIsEqual,
} from "@@/shared/date-time/iso-and-unix";

// Shallow equal
const shallowEqualObject = <T extends object>(a: T, b: T): boolean => {
    // Are they the same object?
    if (Object.is(a, b)) return true;

    // Get the keys of both objects
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    // Are the keys the same length?
    if (aKeys.length !== bKeys.length) return false;
    if (aKeys.length === 0) return true; // Both objects are empty

    // Are the keys the same?
    if (!aKeys.every(key => bKeys.includes(key))) return false;
    if (!bKeys.every(key => aKeys.includes(key))) return false;

    // Are the values the same?
    // - We have already checked that the keys are the same
    // - and that they have all the keys so we can just check and cast a and b as any
    return aKeys.every(key => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const aValue = (a as any)[key];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const bValue = (b as any)[key];
        if (isIsoAndUnix(aValue) && isIsoAndUnix(bValue)) {
            return isIsoAndUnixIsEqual(aValue, bValue);
        }
        return Object.is(aValue, bValue);
    });
};
const shallowCompareObject = <T extends object>(
    a: T,
    b: T,
): {
    unequal?: [string, unknown, string][];
    equal: boolean;
} => {
    const unequal: [string, unknown, string][] = [];

    // Are they the same object?
    if (Object.is(a, b))
        return {
            equal: true,
        };

    // Get the keys of both objects
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);

    for (const key of aKeys) {
        if (!bKeys.includes(key)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            unequal.push([key, (a as any)[key], "missing in b"]);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (!Object.is((a as any)[key], (b as any)[key])) {
            unequal.push([
                key,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (a as any)[key],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                `unequal ${JSON.stringify({ a: (a as any)[key], b: (b as any)[key] })}`,
            ]);
        }
    }
    for (const key of bKeys) {
        if (!aKeys.includes(key)) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            unequal.push([key, (b as any)[key], "missing in a"]);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } else if (!Object.is((a as any)[key], (b as any)[key])) {
            unequal.push([
                key,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (a as any)[key],
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                `unequal ${JSON.stringify({ a: (a as any)[key], b: (b as any)[key] })}`,
            ]);
        }
    }

    return { equal: !unequal.length, unequal };
};

export { shallowCompareObject, shallowEqualObject };
